/**
 * Core types and board geometry for xiangqi.
 *
 * Board: 9 files (a–i, indices 0–8) × 10 ranks (0–9). 90 points total.
 *
 * Square indexing: `square = rank * 9 + file`, so square 0 = a0 and square 89 = i9.
 * Rank 0 is Red's back rank (the bottom of the board from Red's view); rank 9 is
 * Black's back rank. This matches the coordinate move notation used by engines
 * (files a–i, ranks 0–9, e.g. `h2e2`).
 *
 * Red is the side that moves first. Following the xiangqi FEN de-facto standard,
 * Red is the "white"/`w` side and uses UPPERCASE piece letters; Black uses lowercase.
 */

export const FILES = 9;
export const RANKS = 10;
export const NUM_SQUARES = FILES * RANKS; // 90

export enum Color {
  Red = 0,
  Black = 1,
}

export function opponent(c: Color): Color {
  return c === Color.Red ? Color.Black : Color.Red;
}

export enum PieceType {
  King = 0, // 帥 / 將   K/k
  Advisor = 1, // 仕 / 士   A/a
  Elephant = 2, // 相 / 象   B/b  (a.k.a. bishop/minister)
  Horse = 3, // 傌 / 馬   N/n  (a.k.a. knight)
  Rook = 4, // 俥 / 車   R/r  (a.k.a. chariot)
  Cannon = 5, // 炮 / 砲   C/c
  Pawn = 6, // 兵 / 卒   P/p  (a.k.a. soldier)
}

export interface Piece {
  readonly color: Color;
  readonly type: PieceType;
}

/** A board point index in `[0, 90)`. */
export type Square = number;

/** A move from one square to another. Xiangqi has no promotion, so this suffices. */
export interface Move {
  readonly from: Square;
  readonly to: Square;
}

// --- Square geometry -------------------------------------------------------

export function fileOf(sq: Square): number {
  return sq % FILES;
}

export function rankOf(sq: Square): number {
  return (sq / FILES) | 0;
}

export function square(file: number, rank: number): Square {
  return rank * FILES + file;
}

export function isOnBoard(file: number, rank: number): boolean {
  return file >= 0 && file < FILES && rank >= 0 && rank < RANKS;
}

// --- Region helpers --------------------------------------------------------

/**
 * The palace ("九宮") is files d–f (3–5). Red's palace is ranks 0–2, Black's is 7–9.
 * Kings and advisors may never leave it.
 */
export function inPalace(sq: Square, color: Color): boolean {
  const f = fileOf(sq);
  const r = rankOf(sq);
  if (f < 3 || f > 5) return false;
  return color === Color.Red ? r >= 0 && r <= 2 : r >= 7 && r <= 9;
}

/** True if the square is on the given color's own half of the river (has not crossed). */
export function ownSide(sq: Square, color: Color): boolean {
  const r = rankOf(sq);
  return color === Color.Red ? r <= 4 : r >= 5;
}

/** True if a pawn of `color` on this square has crossed the river. */
export function pawnHasCrossed(sq: Square, color: Color): boolean {
  const r = rankOf(sq);
  return color === Color.Red ? r >= 5 : r <= 4;
}

/** Forward rank delta for a pawn/king of the given color (Red advances up, Black down). */
export function forward(color: Color): number {
  return color === Color.Red ? 1 : -1;
}
